import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  mapAdminUser,
  mapDemographics,
  toProfileBody,
  type AdminUserViewModel,
  type AdminUsersPageViewModel,
  type CreateAdminUserInput,
  type UpdateUserProfileInput,
  type UserProfileDraft,
} from '@/features/admin-users/api';
import type { CreateUserRequestDto } from '@/features/admin-users/endpoints';
import {
  platformUsersEndpoints,
  type PlatformRoleDto,
} from '@/features/platform-users/endpoints';

export const DEFAULT_PLATFORM_USERS_PAGE_SIZE = 10;

export const platformUserKeys = {
  user: (uuid: string) => ['platform', 'user', uuid] as const,
  userRoles: (uuid: string) => ['platform', 'user', uuid, 'roles'] as const,
  userDemographics: (uuid: string) => ['platform', 'user', uuid, 'demographics'] as const,
  assignableRoles: ['platform', 'assignable-roles'] as const,
};

/**
 * React-Query surface for the GLOBAL (platform-plane) user directory (PTV2-1383).
 *
 * Mirrors `features/admin-users/api` but targets `/api/v1/platform/users` (always
 * global, never the active tenant) and keys its cache under `['platform','users',…]`
 * so it never collides with the tenant-scoped admin-users cache. The row/view-model
 * mapping and the create/delete input shapes are reused from admin-users.
 */

export interface PlatformUsersQueryParams {
  readonly page: number;
  readonly size: number;
  /** Trimmed search query. Empty string falls back to the unfiltered list endpoint. */
  readonly q?: string;
}

export function platformUsersQueryKey(params: PlatformUsersQueryParams): readonly unknown[] {
  return ['platform', 'users', params.page, params.size, (params.q ?? '').trim()];
}

/**
 * Switches between `GET /platform/users` and `GET /platform/users/search` based on
 * whether `q` is non-empty. `keepPreviousData` keeps the table populated across
 * page/query transitions so the UI doesn't flash a skeleton on every keystroke.
 */
export function usePlatformUsers(
  params: PlatformUsersQueryParams,
): UseQueryResult<AdminUsersPageViewModel> {
  return useQuery<AdminUsersPageViewModel>({
    queryKey: platformUsersQueryKey(params),
    queryFn: async ({ signal }) => {
      const trimmed = (params.q ?? '').trim();
      const page = trimmed.length > 0
        ? await platformUsersEndpoints.searchUsers(
            { q: trimmed, page: params.page, size: params.size },
            { signal },
          )
        : await platformUsersEndpoints.getUsers(
            { page: params.page, size: params.size },
            { signal },
          );
      const rows = page.content.map(mapAdminUser);
      return {
        rows,
        page: page.number ?? params.page,
        size: page.size ?? params.size,
        totalElements: page.totalElements ?? rows.length,
        totalPages:
          page.totalPages ??
          Math.max(1, Math.ceil((page.totalElements ?? rows.length) / Math.max(params.size, 1))),
      };
    },
    placeholderData: keepPreviousData,
  });
}

/** A single global user by guid (for the user detail page). */
export function usePlatformUser(uuid: string): UseQueryResult<AdminUserViewModel> {
  return useQuery<AdminUserViewModel>({
    queryKey: platformUserKeys.user(uuid),
    enabled: uuid.length > 0,
    queryFn: async ({ signal }) => mapAdminUser(await platformUsersEndpoints.getUser(uuid, { signal })),
  });
}

/** A global user's assigned role names (drives the manage-roles dialog's checked state). */
export function usePlatformUserRoles(uuid: string): UseQueryResult<readonly string[]> {
  return useQuery<readonly string[]>({
    queryKey: platformUserKeys.userRoles(uuid),
    enabled: uuid.length > 0,
    queryFn: async ({ signal }) =>
      (await platformUsersEndpoints.getUserRoles(uuid, { signal })).roleNames ?? [],
  });
}

/** A global user's editable demographics (first/last/title). */
export function usePlatformUserDemographics(uuid: string): UseQueryResult<UserProfileDraft> {
  return useQuery<UserProfileDraft>({
    queryKey: platformUserKeys.userDemographics(uuid),
    enabled: uuid.length > 0,
    queryFn: async ({ signal }) =>
      mapDemographics(await platformUsersEndpoints.getUserDemographics(uuid, { signal })),
  });
}

/** The global role catalogue offered by the manage-roles picker. */
export function usePlatformAssignableRoles(): UseQueryResult<readonly PlatformRoleDto[]> {
  return useQuery<readonly PlatformRoleDto[]>({
    queryKey: platformUserKeys.assignableRoles,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => platformUsersEndpoints.getAssignableRoles({ signal }),
  });
}

export interface ReplacePlatformUserRolesInput {
  readonly uuid: string;
  readonly roleNames: readonly string[];
}

export function usePlatformUserMutations(): {
  readonly create: UseMutationResult<AdminUserViewModel, Error, CreateAdminUserInput>;
  readonly remove: UseMutationResult<undefined, Error, string>;
  readonly replaceRoles: UseMutationResult<readonly string[], Error, ReplacePlatformUserRolesInput>;
  readonly deactivate: UseMutationResult<undefined, Error, string>;
  readonly activate: UseMutationResult<undefined, Error, string>;
  readonly updateProfile: UseMutationResult<undefined, Error, UpdateUserProfileInput>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['platform', 'users'] }).catch(() => undefined);
  };
  // Role/status writes also touch a single user's cached record + role list.
  const invalidateUser = (uuid: string): void => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: ['platform', 'user', uuid] }).catch(() => undefined);
  };

  const create = useMutation<AdminUserViewModel, Error, CreateAdminUserInput>({
    meta: { successMessage: 'success.toast.title.user_created' },
    mutationFn: async (input) => {
      const body: CreateUserRequestDto = {
        name: input.name,
        email: input.email,
        ...(input.status !== undefined && input.status !== null && input.status.length > 0
          ? { status: input.status }
          : {}),
      };
      const dto = await platformUsersEndpoints.createUser(body);
      return mapAdminUser(dto);
    },
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string>({
    meta: { successMessage: 'success.toast.title.user_deleted' },
    mutationFn: (uuid) => platformUsersEndpoints.deleteUser(uuid),
    onSettled: invalidate,
  });

  const replaceRoles = useMutation<readonly string[], Error, ReplacePlatformUserRolesInput>({
    mutationFn: async ({ uuid, roleNames }) =>
      (await platformUsersEndpoints.replaceUserRoles(uuid, roleNames)).roleNames ?? [],
    onSettled: (_data, _err, { uuid }) => {
      invalidateUser(uuid);
    },
  });

  const deactivate = useMutation<undefined, Error, string>({
    mutationFn: (uuid) => platformUsersEndpoints.deactivateUser(uuid),
    onSettled: (_data, _err, uuid) => {
      invalidateUser(uuid);
    },
  });

  const activate = useMutation<undefined, Error, string>({
    mutationFn: (uuid) => platformUsersEndpoints.activateUser(uuid),
    onSettled: (_data, _err, uuid) => {
      invalidateUser(uuid);
    },
  });

  const updateProfile = useMutation<undefined, Error, UpdateUserProfileInput>({
    mutationFn: async ({ uuid, draft }) => {
      await platformUsersEndpoints.updateUserProfile(uuid, toProfileBody(draft));
      return undefined;
    },
    onSettled: (_data, _err, { uuid }) => {
      // invalidateUser covers ['platform','user',uuid] and its demographics child.
      invalidateUser(uuid);
      queryClient.invalidateQueries({ queryKey: ['me'] }).catch(() => undefined);
    },
  });

  return { create, remove, replaceRoles, deactivate, activate, updateProfile };
}
